import React from "react";
import { useDashTheme } from "../../hooks/useDashTheme";

/**
 * Data-source vocabulary, in one place.
 *
 * TeamGrid records time from two trackers and they are NOT interchangeable:
 *   app   — the desktop app. Has sessions, start/end times, per-session idle.
 *   agent — the background agent. Reports per HOUR. No session concept at all.
 *
 * They are never summed on the same day: agent time only fills org-local days
 * (and (user, day, project) cells) the app did not record — see
 * `stealth-agent/services/dailyRollup.getSurvivingProjectCells`. Every number
 * in the drill-down is badged with the source that produced it, because
 * "0 sessions" means something completely different from "this source has no
 * sessions to give".
 */

/* PRODUCT RULE: how activity reaches us is INTERNAL. Users are never shown
   which collector produced a figure — the distinction exists so we can be
   honest about missing session detail, not as something to explain to them.
   The map is retained (callers still pass a source key) but every user-visible
   string is neutral, so a stray <SourceChip> can no longer leak the pipeline. */
export const SOURCE_META = {
  app: { label: "Tracked", glyph: "▣", title: "Tracked activity" },
  agent: { label: "Tracked", glyph: "▣", title: "Tracked activity" },
  both: { label: "Tracked", glyph: "▣", title: "Tracked activity" },
};

export function sourceColors(t) {
  return {
    app: t.c.primary,
    appSoft: t.c.primarySoft,
    agent: t.c.ai,
    agentSoft: t.c.aiSoft,
  };
}

/** Small badge. `source` is 'app' | 'agent' | 'both'. Unknown → nothing. */
export function SourceChip({ source, size = "sm" }) {
  const t = useDashTheme();
  const c = sourceColors(t);
  const meta = SOURCE_META[source];
  if (!meta) return null;

  const isBoth = source === "both";
  const fg = source === "agent" ? c.agent : source === "app" ? c.app : t.c.text;
  const bg = isBoth
    ? `linear-gradient(90deg, ${c.appSoft} 50%, ${c.agentSoft} 50%)`
    : source === "agent"
      ? c.agentSoft
      : c.appSoft;

  return (
    <span
      title={meta.title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 3,
        fontSize: size === "xs" ? 9.5 : 10, fontWeight: 600, letterSpacing: 0.2,
        padding: size === "xs" ? "1px 5px" : "2px 6px",
        borderRadius: t.r.xs, whiteSpace: "nowrap",
        color: fg, background: bg,
      }}
    >
      {meta.glyph} {meta.label}
    </span>
  );
}

/**
 * The "Data sources" block: a proportional bar plus one line per source that
 * actually contributed. Always rendered, including the all-app case — a reader
 * should never have to guess whether the agent was involved.
 *
 * `sources` is the server shape:
 *   { app:{minutes,formatted,days?}, agent:{minutes,formatted,days?,unattributedMinutes?},
 *     primary, sessionsCoverAllTime }
 */
export function SourceSplit({ sources }) {
  const t = useDashTheme();
  const c = sourceColors(t);
  if (!sources) return null;

  const app = Math.max(0, Number(sources.app?.minutes) || 0);
  const agent = Math.max(0, Number(sources.agent?.minutes) || 0);
  const total = app + agent;
  if (total <= 0) return null;

  const appPct = Math.round((app / total) * 100);
  const line = (color, label, days, value) => (
    <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11.5, color: t.c.textTertiary, padding: "2px 0" }}>
      <span style={{ width: 7, height: 7, borderRadius: 99, background: color, flexShrink: 0 }} />
      <span>{label}{days != null ? ` · ${days} day${days === 1 ? "" : "s"}` : ""}</span>
      <b style={{ marginLeft: "auto", color: t.c.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</b>
    </div>
  );

  return (
    <div style={{ marginTop: t.s[3], paddingTop: t.s[3], borderTop: `1px dashed ${t.c.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: t.c.textTertiary, marginBottom: 8 }}>
        Data sources
      </div>
      <div style={{ display: "flex", height: 6, borderRadius: 99, overflow: "hidden", marginBottom: 8 }}>
        <span style={{ width: `${appPct}%`, background: c.app }} />
        <span style={{ width: `${100 - appPct}%`, background: c.agent }} />
      </div>
      {app > 0 && line(c.app, "With session detail", sources.app?.days, sources.app?.formatted || `${app}m`)}
      {agent > 0 && line(c.agent, "Without session detail", sources.agent?.days, sources.agent?.formatted || `${agent}m`)}
      {app > 0 && agent > 0 && (
        <div style={{ fontSize: 11, color: t.c.textTertiary, opacity: 0.85, marginTop: 4, lineHeight: 1.5 }}>
          Agent time counts only on days with no app session — never stacked.
        </div>
      )}
      {Number(sources.agent?.unattributedMinutes) > 0 && (
        <div style={{ fontSize: 11, color: t.c.textTertiary, opacity: 0.85, marginTop: 4, lineHeight: 1.5 }}>
          {fmtMins(sources.agent.unattributedMinutes)} of agent time carries no project, so it counts in
          the total but not in the split.
        </div>
      )}
    </div>
  );
}

/** Callout used wherever a source genuinely cannot answer — never show 0. */
export function SourceGap({ children, tone = "warning" }) {
  const t = useDashTheme();
  const color = tone === "warning" ? t.status.warning : t.c.primary;
  const bg = tone === "warning" ? t.status.warningSoft : t.c.primarySoft;
  return (
    <div style={{
      display: "flex", gap: 8, padding: `10px 12px`, borderRadius: t.r.md,
      fontSize: 12, lineHeight: 1.55, background: bg, color: t.c.text,
      boxShadow: `0 0 0 1px ${color}`,
    }}>
      <span aria-hidden="true">⚠</span>
      <span>{children}</span>
    </div>
  );
}

export function fmtMins(mins) {
  const m = Math.round(Number(mins) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h ? `${h}h ${String(r).padStart(2, "0")}m` : `${r}m`;
}
